
import React, { useState, useEffect, useContext } from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header";
import { AuthContext } from "../../../context/authcontext";
import { API_BASE_URL } from "../../../environment";
import TextInput from "../../uiInterface/base-ui/input";
import Select from "react-select";

import { CreditCard } from "lucide-react";
import SettingsTabsNavigation from "../../../components/Tabs/SettingsTabsNavigation";
const MySwal = withReactContent(Swal);
const route = all_routes;


const ProfileLoader = () => (
  <div className="d-flex justify-content-center align-items-center py-5">
    <div className="spinner-border text-primary" role="status">
      <span className="sr-only">Loading...</span>
    </div>
    <span className="ms-2 text-muted">Loading profile data...</span>
  </div>
);

const Profile = () => {
  const { auth, setAuth, selectedTenantId } = useContext(AuthContext);
  const userId = auth?.user_id;
  const authHeader = localStorage.getItem("authCred") || "";

  const primaryColor = "#E41F07";
  const [isLoading, setIsLoading] = useState(false);

  const DEFAULT_AVATAR =
    "https://cloudee-sales-crm-artifact.s3.ap-southeast-7.amazonaws.com/public-artifacts/user/male.png";


  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [region, setRegion] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [address, setAddress] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");


  const [originalData, setOriginalData] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    region: "",
    department_id: null as number | null,
    address: "",
    country: "",
    city: "",
    postal_code: "",
  });


  const [departments, setDepartments] = useState<any[]>([]);
  const [depsLoading, setDepsLoading] = useState(true);
  const [depsError, setDepsError] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string>("");
  const [avatarPreview, setAvatarPreview] = useState<string>("");




  useEffect(() => {
    if (!userId) return;

    const loadAll = async () => {

      setDepsLoading(true);
      setDepsError(null);
      setIsLoading(true);
      try {
        const depsRes = await fetch(`${API_BASE_URL}/department/`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${authHeader}`,
          },
        });
        if (!depsRes.ok) throw new Error(`Departments: ${depsRes.status}`);
        const deps = await depsRes.json();
        setDepartments(deps);
      } catch (err) {
        console.error(err);
        setDepsError("Could not load departments. Please try again.");
      } finally {
        setDepsLoading(false);
      }


      try {
        const userRes = await fetch(`${API_BASE_URL}/user/${userId}/`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${authHeader}`,
          },
        });
        if (!userRes.ok) throw new Error(`User: ${userRes.status}`);
        const data = await userRes.json();


        setFirstName(data.first_name || "");
        setLastName(data.last_name || "");
        setPhoneNumber(data.phone || "");
        setEmail(data.email || "");
        setRegion(data.region || "");
        setSelectedDepartment(
          data.department_id != null ? String(data.department_id) : ""
        );
        setAddress(data.address || "");
        setCountry(data.country || "");
        setCity(data.city || "");
        setPostalCode(data.postal_code || "");


        setOriginalData({
          first_name: data.first_name || "",
          last_name: data.last_name || "",
          phone: data.phone || "",
          email: data.email || "",
          region: data.region || "",
          department_id: data.department_id,
          address: data.address || "",
          country: data.country || "",
          city: data.city || "",
          postal_code: data.postal_code || "",
        });




        setAvatarUrl(data.avatar_url || "");

        setAvatarPreview("");
      } catch (err) {
        console.error(err);
      } finally {

        setIsLoading(false);
      }
    };

    loadAll();
  }, [authHeader, userId]);

  useEffect(() => {
    const hasUnsaved =
      firstName !== originalData.first_name ||
      lastName !== originalData.last_name ||
      phoneNumber !== originalData.phone ||
      email !== originalData.email ||
      region !== originalData.region ||
      address !== originalData.address ||
      country !== originalData.country ||
      city !== originalData.city ||
      postalCode !== originalData.postal_code ||
      selectedDepartment !==
      (originalData.department_id != null
        ? String(originalData.department_id)
        : "") ||
      avatarFile !== null;

    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsaved) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [
    firstName,
    lastName,
    phoneNumber,
    email,
    region,
    address,
    country,
    city,
    postalCode,
    selectedDepartment,
    originalData,
    avatarFile,
  ]);


  const handleSave = async () => {

    const payload: Record<string, any> = {};
    if (firstName !== originalData.first_name) payload.first_name = firstName;
    if (lastName !== originalData.last_name) payload.last_name = lastName;
    if (phoneNumber !== originalData.phone) payload.phone = phoneNumber;
    if (email !== originalData.email) payload.email = email;
    if (region !== originalData.region) payload.region = region;
    if (address !== originalData.address) payload.address = address;
    if (country !== originalData.country) payload.country = country;
    if (city !== originalData.city) payload.city = city;
    if (postalCode !== originalData.postal_code)
      payload.postal_code = postalCode;

    const origDept =
      originalData.department_id != null
        ? String(originalData.department_id)
        : "";
    if (selectedDepartment !== origDept) {
      payload.department_id = selectedDepartment
        ? Number(selectedDepartment)
        : null;
    }

    if (!Object.keys(payload).length && !avatarFile) {
      MySwal.fire({
        icon: "info",
        title: "No changes detected",
        text: "You didn't update anything.",
        confirmButtonText: "OK",
      });
      return;
    }


    MySwal.fire({
      title: "Updating…",
      allowOutsideClick: false,
      didOpen: () => MySwal.showLoading(),
    });


    const formData = new FormData();
    Object.entries(payload).forEach(([k, v]) => formData.append(k, String(v)));
    if (avatarFile) {
      formData.append("avatar_url", avatarFile);
    }

    try {
      const res = await fetch(`${API_BASE_URL}/user/${userId}/`, {
        method: "PATCH",
        headers: {
          Authorization: `Basic ${authHeader}`,
        },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Update failed");


      setOriginalData((o) => ({ ...o, ...payload }));
      const fullRes = await fetch(`${API_BASE_URL}/user/${userId}/`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${authHeader}`,
        },
      });
      const fullData = await fullRes.json();
      if (!fullRes.ok) throw new Error(fullData.detail || "Reload failed");


      setAuth((prevAuth: any) => ({
        ...prevAuth,
        ...fullData,

        tenants: prevAuth?.tenants || []
      }));
      setAvatarUrl(fullData.avatar_url || DEFAULT_AVATAR);
      setAvatarPreview("");
      setAvatarFile(null);


      MySwal.close();
      MySwal.fire({
        icon: "success",
        title: "Saved!",
        text: avatarFile
          ? "Your profile  have been updated."
          : "Your profile changes have been saved.",
        confirmButtonText: "OK",
      });
    } catch (err) {
      console.error("Failed to update user:", err);
      MySwal.close();
      MySwal.fire({
        icon: "error",
        title: "Error",
        text: avatarFile
          ? "There was a problem uploading your avatar."
          : "There was a problem saving your changes.",
        confirmButtonText: "OK",
      });
    }
  };

  const handleCancel = async () => {

    const hasChanges =
      firstName !== originalData.first_name ||
      lastName !== originalData.last_name ||
      phoneNumber !== originalData.phone ||
      email !== originalData.email ||
      region !== originalData.region ||
      address !== originalData.address ||
      country !== originalData.country ||
      city !== originalData.city ||
      postalCode !== originalData.postal_code ||
      selectedDepartment !==
      (originalData.department_id != null
        ? String(originalData.department_id)
        : "") ||
      avatarFile !== null;

    if (!hasChanges) return;

    const result = await MySwal.fire({
      icon: "warning",
      title: "Discard Changes?",
      text: "Are you sure you want to cancel your changes?",
      showCancelButton: true,
      confirmButtonText: "Yes, discard",
      cancelButtonText: "No, keep editing",
      reverseButtons: true,
      focusCancel: true,
    });

    if (result.isConfirmed) {

      setFirstName(originalData.first_name);
      setLastName(originalData.last_name);
      setPhoneNumber(originalData.phone);
      setEmail(originalData.email);
      setRegion(originalData.region);
      setSelectedDepartment(
        originalData.department_id != null
          ? String(originalData.department_id)
          : ""
      );
      setAddress(originalData.address);
      setCountry(originalData.country);
      setCity(originalData.city);
      setPostalCode(originalData.postal_code);
      setAvatarFile(null);
      setAvatarPreview("");

      MySwal.fire({
        icon: "info",
        title: "Changes discarded",
        text: "Your unsaved changes have been reset.",
        timer: 2000,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timerProgressBar: true,
      });
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col-sm-4">
              <h4 className="page-title">Settings</h4>
            </div>
            <div className="col-sm-8 text-sm-end">
              <div className="head-icons">
                <CollapseHeader />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body pb-0 pt-2">
            <SettingsTabsNavigation activeTab="general" variant="bottom" includeIcons={true} context="profile" />
          </div>
        </div>

        <div className="row">
          {/* Sidebar */}
          <div className="col-xl-3 col-lg-12 theiaStickySidebar">
            <div className="card">
              <div className="card-body">
                <div className="settings-sidebar">
                  <div className="list-group list-group-flush settings-sidebar">
                    <Link to={route.profile} className="fw-medium active">
                      Profile
                    </Link>
                    <Link to={route.security} className="fw-medium">
                      Security
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Form */}
          <div className="col-xl-9 col-lg-12">
            <div className="card">
              <div className="card-body">
                <h4 className="fw-semibold">Profile Settings</h4>
                <p>Provide the information below</p>
                {isLoading ? (
                  <ProfileLoader />
                ) : (
                  <form>
                    {/* Avatar Upload */}
                    <div className="mb-3">
                      <div className="profile-upload">
                        <div
                          className="profile-upload-img"
                          style={{

                            backgroundImage: `url(${avatarPreview || avatarUrl || DEFAULT_AVATAR
                              })`,
                            width: 120,
                            height: 120,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            border:
                              avatarPreview || avatarUrl
                                ? "none"
                                : "2px dashed #ced4da",
                          }}
                        >
                          {/* only show the camera icon when there’s genuinely no image */}
                          {!(avatarPreview || avatarUrl) && (
                            <span>
                              {/* <i className="ti ti-photo" /> */}
                            </span>
                          )}

                          {/* remove-button stays the same */}
                          {!!avatarPreview && (
                            <button
                              type="button"
                              className="profile-remove"
                              onClick={() => {
                                setAvatarFile(null);
                                setAvatarPreview("");
                              }}
                            >
                              <i className="feather-x" />
                            </button>
                          )}
                        </div>

                        <div className="profile-upload-content">
                          <label className="profile-upload-btn">
                            <i className="ti ti-file-broken" /> Upload File
                            <input
                              type="file"
                              accept="image/*"
                              className="input-img"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                setAvatarFile(file);
                                setAvatarPreview(URL.createObjectURL(file));
                              }}
                            />
                          </label>
                          <p>JPG, GIF or PNG. Max size of 800K</p>
                        </div>
                      </div>
                    </div>

                    {/* Form Fields */}
                    <div className="border-bottom mb-3">
                      <div className="row">
                        {/* First Name */}
                        <div className="col-md-4">
                          <TextInput
                            label="First Name"
                            required
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            placeholder="Enter first name"
                          />
                        </div>

                        {/* Last Name */}
                        <div className="col-md-4">
                          <TextInput
                            label="Last Name"
                            required
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            placeholder="Enter last name"
                          />
                        </div>

                        {/* Phone */}
                        <div className="col-md-4">
                          <div className="mb-3">
                            <label className="form-label">
                              Phone Number <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={phoneNumber}
                              onChange={(e) => setPhoneNumber(e.target.value)}
                            />
                          </div>
                        </div>

                        {/* Email */}
                        <div className="col-md-4">
                          <div className="mb-3">
                            <label className="form-label">
                              Email <span className="text-danger">*</span>
                            </label>
                            <input
                              type="email"
                              className="form-control"
                              value={email}
                              disabled
                            />
                          </div>
                        </div>

                        {/* Region */}
                        {/* <div className="col-md-4">
                        <div className="mb-3">
                          <label className="form-label">
                            Region <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={region}
                            onChange={(e) => setRegion(e.target.value)}
                          />
                        </div>
                      </div> */}

                        {/* Department */}
                        <div className="col-md-4">
                          <div className="mb-3">
                            <label className="form-label">
                              Department <span className="text-danger">*</span>
                            </label>
                            {depsLoading ? (
                              <div>Loading departments…</div>
                            ) : depsError ? (
                              <div className="text-danger">{depsError}</div>
                            ) : (
                              <Select
                                options={departments.map((d) => ({
                                  value: String(d.department_id),
                                  label: d.department_display_name,
                                }))}
                                value={
                                  selectedDepartment
                                    ? {
                                      value: selectedDepartment,
                                      label:
                                        departments.find(
                                          (d) =>
                                            String(d.department_id) ===
                                            selectedDepartment
                                        )?.department_display_name || "",
                                    }
                                    : null
                                }
                                onChange={(opt) =>
                                  setSelectedDepartment(opt?.value || "")
                                }
                                placeholder="Select Department…"
                                isClearable
                                theme={(theme) => ({
                                  ...theme,
                                  colors: {
                                    ...theme.colors,
                                    primary: primaryColor,
                                    primary25: "transparent",
                                    neutral20: "#ced4da",
                                    neutral30: "#ced4da",
                                  },
                                })}
                                styles={{
                                  control: (base) => ({
                                    ...base,
                                    borderColor: "#ced4da",
                                    boxShadow: "none",
                                    "&:hover": { borderColor: "#ced4da" },
                                  }),
                                  option: (base, { isSelected, isFocused }) => ({
                                    ...base,
                                    backgroundColor: isSelected
                                      ? primaryColor
                                      : "transparent",
                                    color: isSelected
                                      ? "#fff"
                                      : isFocused
                                        ? primaryColor
                                        : "#212529",
                                    cursor: "pointer",
                                  }),
                                  valueContainer: (base) => ({
                                    ...base,
                                    padding: ".375rem .75rem",
                                  }),
                                  dropdownIndicator: (base) => ({
                                    ...base,
                                    color: "#6c757d",
                                    "&:hover": { color: "#6c757d" },
                                  }),
                                  clearIndicator: (base) => ({
                                    ...base,
                                    color: "#6c757d",
                                    "&:hover": { color: "#6c757d" },
                                  }),
                                }}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Address (unchanged) */}
                    <div className="border-bottom mb-3 pb-3">
                      <h5 className="fw-semibold mb-1">Address (Optional)</h5>
                      <p>Please enter the address details</p>
                    </div>
                    <div className="row">
                      <div className="col-md-12">
                        <div className="mb-3">
                          <label className="form-label">
                            Address
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Country
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={country}
                            onChange={(e) => setCountry(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            State / Province{" "}

                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={region}
                            onChange={(e) => setRegion(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            City
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Postal Code
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={postalCode}
                            onChange={(e) => setPostalCode(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Save */}
                    <div>
                      <button
                        type="button"
                        className="btn btn-outline-secondary me-2"
                        onClick={(e) => {
                          handleCancel();
                          e.currentTarget.blur();
                        }}
                      >
                        Cancel
                      </button>

                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={(e) => {
                          handleSave();
                          e.currentTarget.blur();
                        }}
                      >
                        Save Changes
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
